import React, { KeyboardEvent, RefObject, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ReactComponent as ArrowDown } from '@/assets/icons/chevron-down.svg';
import { ReactComponent as ArrowUp } from '@/assets/icons/chevron-up.svg';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import { useOnClickOutside } from '@/hooks/common/use-on-click-outside/useOnClickOutside';
import classNames from 'classnames';
import '@/components/common/select/Select.scss';
import './CategoryCombobox.scss';

export interface CategoryComboboxOption {
    id: number;
    name: string;
}

export interface CategoryComboboxProps {
    options: CategoryComboboxOption[];
    inputValue: string;
    onInputValueChange: (value: string) => void;
    onOptionSelect: (option: CategoryComboboxOption) => void;
    selectContainerRef?: RefObject<HTMLDivElement | null>;
    placeholder?: string;
    className?: string;
    headClassName?: string;
    optionClassName?: string;
    disabled?: boolean;
    id?: string;
    notFoundMessage?: string;
}

export const CategoryCombobox = ({
    options,
    inputValue,
    onInputValueChange,
    onOptionSelect,
    selectContainerRef,
    placeholder,
    className,
    headClassName,
    optionClassName,
    disabled = false,
    id,
    notFoundMessage = COMMON_TEXT_ADMIN.LIST.NOT_FOUND,
}: CategoryComboboxProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const setContainerRef = (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (selectContainerRef) {
            selectContainerRef.current = node;
        }
    };

    const normalizedQuery = inputValue.trim().replace(/\s+/g, ' ').toLowerCase();

    const isExactMatch = useMemo(
        () => options.some((option) => option.name.trim().replace(/\s+/g, ' ').toLowerCase() === normalizedQuery),
        [options, normalizedQuery],
    );

    const filteredOptions = useMemo(() => {
        if (normalizedQuery === '' || (!isSearching && isExactMatch)) {
            return options;
        }

        return options.filter((option) => {
            const normalizedOptionName = option.name.trim().replace(/\s+/g, ' ').toLowerCase();
            return normalizedOptionName.includes(normalizedQuery);
        });
    }, [options, normalizedQuery, isSearching, isExactMatch]);

    const openDropdown = () => {
        if (disabled) return;
        setIsOpen(true);
    };

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsSearching(false);
    }, []);

    useEffect(() => {
        if (disabled) {
            closeDropdown();
        }
    }, [disabled, closeDropdown]);

    useOnClickOutside({
        ignoreClickRefs: [containerRef],
        onOutsideClick: closeDropdown,
        isDisabled: !isOpen,
    });

    const selectOption = (option: CategoryComboboxOption) => {
        onOptionSelect(option);
        closeDropdown();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsSearching(true);
        onInputValueChange(event.target.value);
        setHighlightedIndex(-1);
        openDropdown();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!isOpen) {
                openDropdown();
                return;
            }
            setHighlightedIndex((previousIndex) =>
                previousIndex < filteredOptions.length - 1 ? previousIndex + 1 : previousIndex,
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((previousIndex) => (previousIndex > 0 ? previousIndex - 1 : 0));
        } else if (event.key === 'Enter') {
            if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                event.preventDefault();
                selectOption(filteredOptions[highlightedIndex]);
            } else {
                closeDropdown();
            }
        } else if (event.key === 'Escape') {
            closeDropdown();
        }
    };

    return (
        <div
            ref={setContainerRef}
            className={classNames('select', className, {
                'select-opened': isOpen,
                'select-closed': !isOpen,
                'select-disabled': disabled,
            })}
        >
            <label htmlFor={inputId} className={classNames('select-head', headClassName)}>
                <input
                    ref={inputRef}
                    id={inputId}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={openDropdown}
                    onClick={openDropdown}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                />
                {isOpen ? <ArrowUp /> : <ArrowDown />}
            </label>

            {isOpen && !disabled && (
                <div className="select-options">
                    {filteredOptions.length === 0 ? (
                        <div className="category-combobox-not-found">{notFoundMessage}</div>
                    ) : (
                        filteredOptions.map((option, index) => {
                            const isSelected =
                                option.name.trim().replace(/\s+/g, ' ').toLowerCase() === normalizedQuery;
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={classNames(optionClassName, {
                                        'category-combobox-option-active': index === highlightedIndex,
                                        'select-options-selected': isSelected,
                                    })}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        selectOption(option);
                                    }}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                >
                                    <span>{option.name}</span>
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};
